/**
 * Per-version tag-change summary — "+N −M tags" against the previous listed
 * version, expandable to the exact added/removed tag keys, so an author can
 * see what a version changed without downloading two docx files. Renders
 * nothing when the version changed no tags.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { BodyTagDiff } from '../../utils/versionTagDiff';

export default function VersionTagDiff({ added, removed }: BodyTagDiff) {
  const [expanded, setExpanded] = useState(false);

  if (added.length === 0 && removed.length === 0) return null;

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center gap-1 text-caption font-medium text-slate-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-brand-400"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
        )}
        {`+${added.length} −${removed.length} tags`}
      </button>
      {expanded && (
        <ul className="mt-1 space-y-0.5 font-mono text-caption">
          {added.map((tag) => (
            <li key={`+${tag}`} className="text-emerald-700 dark:text-emerald-400">
              {'+ '}
              <span>{tag}</span>
            </li>
          ))}
          {removed.map((tag) => (
            <li key={`-${tag}`} className="text-red-700 dark:text-red-400">
              {'− '}
              <span>{tag}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
